// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Charge } from '@/billing/api/types';
import { BarChart } from '@/components/bar-chart';
import {
  ChargeStatus,
  ChargeStatusColorMap,
  ChargeStatusLabelMap,
  ChargeStatuses,
} from '@/constants/charge-status';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { CurrencyDecimal } from '@/currency/utilities/decimal';
import { formatAmountWithCurrency } from '@/currency/utilities/format';
import { isZeroDecimal } from '@/currency/utilities/validate';
import { Project } from '@/project/api/types';
import { Stack, Typography, useTheme } from '@mui/material';
import { Decimal } from 'decimal.js';
import { useMemo } from 'react';

const DEFAULT_CHART_ISO_CODE: IsoCurrencyCode = 'USD';

interface ProjectBillingChartDatum {
  label: string;
  totalAmount: number;
  totalEvents: number;
  amountsByStatus: Record<ChargeStatus, number>;
}

function calculateChargeTotals(charges: Charge[]): ProjectBillingChartDatum {
  const decimalAmountsByStatus: Record<ChargeStatus, Decimal> = {
    PLANNED: new CurrencyDecimal(0),
    BILLABLE: new CurrencyDecimal(0),
    INVOICED: new CurrencyDecimal(0),
    CANCELLED: new CurrencyDecimal(0),
  };

  for (const charge of charges) {
    const status = charge.status;
    if (status in decimalAmountsByStatus) {
      decimalAmountsByStatus[status] = decimalAmountsByStatus[status].plus(
        charge.totalPrice.amount
      );
    }
  }

  const totalAmount = Object.values(decimalAmountsByStatus).reduce(
    (sum, amount) => sum.plus(amount),
    new CurrencyDecimal(0)
  );

  return {
    label: 'Charge summary',
    totalAmount: totalAmount.toNumber(),
    totalEvents: charges.length,
    amountsByStatus: {
      PLANNED: decimalAmountsByStatus.PLANNED.toNumber(),
      BILLABLE: decimalAmountsByStatus.BILLABLE.toNumber(),
      INVOICED: decimalAmountsByStatus.INVOICED.toNumber(),
      CANCELLED: decimalAmountsByStatus.CANCELLED.toNumber(),
    },
  };
}

/**
 * Props for the ProjectBillingChart component
 */
interface ProjectBillingChartProps {
  /**
   * Array of projects to display billing information for
   */
  projects: Project[];
  /**
   * The variant of the chart to display
   * @default 'full'
   */
  variant?: 'full' | 'mini';

  /**
   * Whether to show the bottom total value
   * @default false
   */
  showBottomTotal?: boolean;

  /**
   * Custom data to use instead of calculating from projects. Only the billing
   * totals are consumed; the datum label is supplied by the chart itself.
   */
  customData?: Pick<
    ProjectBillingChartDatum,
    'totalAmount' | 'totalEvents' | 'amountsByStatus'
  >;

  /**
   * ISO currency code used to format the chart's amounts. Defaults to USD for
   * the legacy per-project path, which has no aggregated currency.
   */
  currencyCode?: IsoCurrencyCode;
}

/**
 * A component that displays a bar chart of project billing segments.
 * Can be rendered in full or mini variant.
 */
export function ProjectBillingChart({
  projects,
  variant = 'full',
  showBottomTotal = false,
  customData,
  currencyCode: currencyCode = DEFAULT_CHART_ISO_CODE,
}: ProjectBillingChartProps) {
  const theme = useTheme();
  const billingData = useMemo(
    () =>
      calculateChargeTotals(
        projects.flatMap((project) =>
          project.items.flatMap((lineItem) => lineItem.charges)
        )
      ),
    [projects]
  );

  const fractionDigits = isZeroDecimal(currencyCode) ? 0 : 2;

  // Merge amountsByStatus into a flat object so it matches BarChart's expected shape
  const rawData = customData || billingData;

  // Convert to BarChart-compatible datum structure
  const chartDatum: Record<ChargeStatus, number> & {
    totalCount: number;
  } = {
    ...rawData.amountsByStatus,
    totalCount: rawData.totalAmount,
  };

  // The "no charges" state only applies to the legacy per-project path. A
  // per-currency section (customData) only exists when that currency has
  // charges, so an all-zero section falls through to the "zero total"
  // message below rather than wrongly claiming there are no charges.
  if (!customData && rawData.totalEvents === 0) {
    return (
      <Typography color="text.secondary" textAlign="center" py={2}>
        {projects.length === 1
          ? 'No charges found for this project'
          : 'No charges found for the selected projects'}
      </Typography>
    );
  }

  if (rawData.totalAmount === 0) {
    return (
      <Typography color="text.secondary" textAlign="center" py={2}>
        {projects.length === 1
          ? 'Charges in this project have a zero total'
          : 'Charges in the selected projects have a zero total'}
      </Typography>
    );
  }

  return (
    <Stack>
      <BarChart
        keys={ChargeStatuses}
        data={[chartDatum]}
        colors={ChargeStatuses.map((status) =>
          ChargeStatusColorMap(status, theme)
        )}
        emptyState="No charge data available"
        getLabelForKey={ChargeStatusLabelMap}
        getLabelForDatum={() => 'Charge summary'}
        hideDatumTextLabels
        hideTotalValueLabels={variant === 'mini'}
        showSegmentValueLabels={variant === 'full'}
        pluralizeTotalValue
        pluralizeTotalOptions={{
          singular: '',
          plural: '',
          format: {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          },
        }}
        pluralizeLabelOptions={{
          singular: '',
          plural: '',
          format: {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          },
        }}
        pluralizeTooltipOptions={{
          singular: '',
          plural: '',
          format: {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
          },
        }}
        minimumHeight={0}
      />
      {showBottomTotal && (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          paddingX={2}
          textAlign="right"
        >
          {formatAmountWithCurrency(
            new CurrencyDecimal(rawData.totalAmount),
            currencyCode,
            { sparse: true }
          )}
        </Typography>
      )}
    </Stack>
  );
}
